import { type Primitive } from '@sentry/core';
import * as Sentry from '@sentry/react';
import { type ReactNode } from 'react';

import { GenericErrorBanner } from '../GenericErrorBanner';

interface ErrorBoundaryProps {
  app?: 'bo' | 'fo' | 'po';
  children: ReactNode;
  context?: {
    tags?: { [key: string]: Primitive };
  };
  module?: string;
  shouldReloadPageWhenResettingError?: boolean;
  submodule?: string;
  tribe?:
    | 'admin-xp-squad'
    | 'builder-tribe'
    | 'evolution-tribe'
    | 'user-journey-tribe';
}

const ErrorBoundary = ({
  children,
  context,
  tribe,
  app,
  module,
  shouldReloadPageWhenResettingError = false,
  submodule,
}: ErrorBoundaryProps) => (
  <Sentry.ErrorBoundary
    beforeCapture={(scope) => {
      if (context?.tags) {
        scope.setTags(context.tags);
      }

      if (tribe) {
        scope.setTag('tribe', tribe);
      }

      if (app) {
        scope.setTag('app', app);
      }

      if (module) {
        scope.setTag('module', module);
      }

      if (submodule) {
        scope.setTag('submodule', submodule);
      }

      scope.setTag('page_locale', I18n.locale);
    }}
    fallback={(props) => (
      <GenericErrorBanner
        {...props}
        resetError={() => {
          if (shouldReloadPageWhenResettingError) {
            window.location.reload();
          } else {
            props.resetError();
          }
        }}
      />
    )}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default ErrorBoundary;

type TribeErrorBoundary = Omit<ErrorBoundaryProps, 'tribe'>;

export const EvErrorBoundary = (props: TribeErrorBoundary) => (
  <ErrorBoundary {...props} tribe="evolution-tribe" />
);

export const UjErrorBoundary = (props: TribeErrorBoundary) => (
  <ErrorBoundary {...props} tribe="user-journey-tribe" />
);

export const BuilderErrorBoundary = (props: TribeErrorBoundary) => (
  <ErrorBoundary {...props} tribe="builder-tribe" />
);

export const AdminXpErrorBoundary = (props: TribeErrorBoundary) => (
  <ErrorBoundary {...props} tribe="admin-xp-squad" />
);
